import { createContext, useContext, useSyncExternalStore, useState, type ReactNode } from 'react';

const User = createContext<string | null>(null);
const choices = new Set([5, 10, 20, 30, 50, 75, 100]);
const eventName = 'tosho-pagination-preference';
const temporaryValues = new Map<string, number>();
/** Un seul réglage de confort par utilisateur, partagé par toutes les listes
 * ajustables. Il survit au logout ; aucune donnée métier ou preuve de droit. */
export function PaginationPreferences({
  userId,
  children,
}: {
  readonly userId: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return <User.Provider value={userId}>{children}</User.Provider>;
}

export function usePaginationPageSize(): readonly [number, (size: number) => void] {
  const userId = useContext(User);
  const [temporary, setTemporary] = useState(20);
  const key = userId ? 'tosho:pagination:v1:' + encodeURIComponent(userId) : null;
  const subscribe = (notify: () => void): (() => void) => {
    const storage = (event: StorageEvent): void => {
      if (event.key === key && key) {
        temporaryValues.delete(key);
        notify();
      }
    };
    window.addEventListener('storage', storage);
    window.addEventListener(eventName, notify);
    return () => {
      window.removeEventListener('storage', storage);
      window.removeEventListener(eventName, notify);
    };
  };
  const read = (): number => {
    if (!key) return 20;
    if (temporaryValues.has(key)) return temporaryValues.get(key)!;
    try {
      const size = Number(window.localStorage.getItem(key));
      return choices.has(size) ? size : 20;
    } catch {
      return 20;
    }
  };
  const size = useSyncExternalStore(subscribe, read, () => 20);
  return [
    key ? size : temporary,
    (next) => {
      if (!choices.has(next)) return;
      setTemporary(next);
      if (!key) return;
      temporaryValues.set(key, next);
      try {
        window.localStorage.setItem(key, String(next));
        window.dispatchEvent(new Event(eventName));
      } catch {
        window.dispatchEvent(new Event(eventName));
      }
    },
  ];
}
